import { useSyncExternalStore } from 'react'
import { Button, Dropdown, Popover, Tag, Tooltip } from 'antd'
import { CopyOutlined, DeleteOutlined } from '@ant-design/icons'
import { diagnosticsStore } from './diagnostic-store'
import { formatDiagnosticSummary } from './format-diagnostic'
import { buildIssuePackage, formatIssuePackageJson, formatIssuePackageMarkdown } from './issue-package'
import { getIssueBusinessContext } from './issue-package-context'

function getEventLabel(event: ReturnType<typeof diagnosticsStore.getEvents>[number]): string {
  if (event.request) return `${event.request.method} ${event.request.path}`
  return event.error.message
}

function formatEventTime(at: number): string {
  return new Intl.DateTimeFormat('zh-CN', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).format(at)
}

export default function DiagnosticsPanel() {
  const events = useSyncExternalStore(
    (listener) => diagnosticsStore.subscribe(listener),
    () => diagnosticsStore.getEvents(),
    () => diagnosticsStore.getEvents()
  )
  const copySummaries = async (eventsToCopy: typeof events) => {
    if (eventsToCopy.length === 0 || !navigator.clipboard) return
    await navigator.clipboard.writeText(eventsToCopy.map(formatDiagnosticSummary).join('\n\n---\n\n'))
  }
  const createIssuePackage = (event: typeof events[number]) => buildIssuePackage({
    event,
    actions: diagnosticsStore.getActionsBefore(event.at),
    businessContext: getIssueBusinessContext(event.pageUrl),
  })
  const copyIssuePackage = async (event: typeof events[number], format: 'markdown' | 'json') => {
    if (!navigator.clipboard) return
    const issuePackage = createIssuePackage(event)
    const text = format === 'json' ? formatIssuePackageJson(issuePackage) : formatIssuePackageMarkdown(issuePackage)
    await navigator.clipboard.writeText(text)
  }

  return (
    <section className="rounded bg-white p-3 shadow-sm">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-medium text-gray-800">故障记录</div>
          <div className="mt-0.5 text-xs text-gray-400">仅保留本页面最近的失败请求和运行时异常</div>
        </div>
        <div className="flex items-center gap-1">
          <Tag color={events.length > 0 ? 'blue' : undefined} className="m-0">{events.length} 条</Tag>
          <Tooltip title="清空故障记录">
            <Button
              aria-label="清空故障记录"
              type="text"
              shape="circle"
              icon={<DeleteOutlined />}
              disabled={events.length === 0}
              onClick={() => diagnosticsStore.clear()}
            />
          </Tooltip>
        </div>
      </div>

      {events.length === 0 ? (
        <div className="rounded bg-gray-50 px-3 py-3 text-xs text-gray-400">暂无故障记录</div>
      ) : (
        <div className="space-y-2">
          <div className="max-h-52 space-y-1.5 overflow-y-auto pr-1">
            {events.map((event) => {
              const status = event.request?.status
              return (
                <Popover
                  key={event.id}
                  content={<pre className="m-0 max-h-80 max-w-96 overflow-auto whitespace-pre-wrap break-all text-xs">{formatIssuePackageMarkdown(createIssuePackage(event))}</pre>}
                  trigger="hover"
                  placement="left"
                >
                  <div className="flex items-center gap-1 rounded bg-gray-50 px-3 py-2">
                    <div className="min-w-0 flex-1">
                      <span className="flex items-center justify-between gap-2">
                        <span className="min-w-0 truncate text-xs font-medium text-gray-700">{getEventLabel(event)}</span>
                        <span className="flex shrink-0 items-center gap-1">
                          <span className="text-xs text-gray-400">{formatEventTime(event.at)}</span>
                          {event.occurrences && event.occurrences > 1 ? (
                            <span className="text-xs text-gray-400">发生 {event.occurrences} 次</span>
                          ) : null}
                          <Tag color={status && status >= 500 ? 'error' : 'warning'} className="m-0">
                            {status ?? '运行时'}
                          </Tag>
                        </span>
                      </span>
                      <span className="mt-1 block truncate text-xs text-gray-400">{event.error.message}</span>
                    </div>
                  <Dropdown
                    trigger={['click']}
                    menu={{
                      items: [
                        { key: 'markdown', label: '复制 Markdown' },
                        { key: 'json', label: '复制 JSON' },
                      ],
                      onClick: ({ key }) => void copyIssuePackage(event, key === 'json' ? 'json' : 'markdown'),
                    }}
                  >
                    <Button
                      aria-label={`复制 ${getEventLabel(event)} 故障记录`}
                      type="text"
                      icon={<CopyOutlined />}
                      size="small"
                    />
                  </Dropdown>
                  </div>
                </Popover>
              )
            })}
          </div>
          <Button
            block
            aria-label="复制全部"
            icon={<CopyOutlined />}
            onClick={() => void copySummaries(events)}
          >
            复制全部
          </Button>
        </div>
      )}
    </section>
  )
}
